// getColor.ts
import { alpha, darken, getContrastRatio, lighten, Palette, Theme } from '@mui/material'

import { alphaByTheme } from './colorUtils.ts' // Asumo que tienes esto

export const COLOR_TINTS = {
  soft: {
    light: 12,
    dark: 14,
  },
  strong: {
    light: 20,
    dark: 24,
  },
}

type StyledPaletteColors = {
  [K in keyof Palette]: Palette[K] extends { main: string } ? K : never
}[keyof Palette]

// =========================================================================
// 1. UTILIDADES MATEMÁTICAS CENTRALIZADAS
// =========================================================================

/**
 * Garantiza un ratio de contraste mínimo (WCAG).
 * Exportado para que themeColors.ts pueda usarlo.
 */
export const ensureContrast = (
  color: string,
  background: string,
  isDark: boolean,
  minRatio: number = 4.5,
): string => {
  let result = color
  let ratio = getContrastRatio(result, background)

  if (ratio >= minRatio) return result

  const step = 0.05
  let amount = 0

  while (ratio < minRatio && amount < 0.9) {
    amount += step
    result = isDark ? lighten(color, amount) : darken(color, amount)
    ratio = getContrastRatio(result, background)
  }

  return result
}

/**
 * Motor centralizado para generar todos los estados avanzados (Soft/Strong/Borders).
 * Lo usa themeColors.ts para inicializar el tema, y getColor() para colores custom HEX.
 */
export const generateAdvancedStatesFromHex = (mainColor: string, paperBg: string, isDark: boolean) => {
  const softTint = isDark ? COLOR_TINTS.soft.dark : COLOR_TINTS.soft.light
  const strongTint = isDark ? COLOR_TINTS.strong.dark : COLOR_TINTS.strong.light

  const rawSoftText = isDark ? lighten(mainColor, 0.55) : darken(mainColor, 0.5)
  const rawStrongText = isDark ? lighten(mainColor, 0.7) : darken(mainColor, 0.65)
  const targetRatio = isDark ? 4.5 : 5.5

  return {
    softBgColor: `color-mix(in srgb, ${mainColor} ${softTint}%, ${paperBg})`,
    softTextColor: ensureContrast(rawSoftText, paperBg, isDark, targetRatio),

    strongBgColor: `color-mix(in srgb, ${mainColor} ${strongTint}%, ${paperBg})`,
    strongTextColor: ensureContrast(rawStrongText, paperBg, isDark, targetRatio),

    hoverBgColor: alpha(mainColor, isDark ? 0.12 : 0.04),
    faintBgColor: alpha(mainColor, isDark ? 0.06 : 0.02),

    borderColor: alpha(mainColor, isDark ? 0.3 : 0.2),
    borderSoft: alpha(mainColor, isDark ? 0.2 : 0.15),
    borderMedium: alpha(mainColor, isDark ? 0.4 : 0.3),
    borderStrong: alpha(mainColor, isDark ? 0.7 : 0.6),
  }
}

// =========================================================================
// 2. FUNCIÓN PRINCIPAL DE CONSUMO (Componentes React)
// =========================================================================

export const getColor = (theme: Theme, bgColor?: StyledPaletteColors | string) => {
  const isDark = theme.palette.mode === 'dark'
  const paperBg = theme.palette.background.paper

  // 1. FONDO NEUTRO
  if (!bgColor) {
    const tintPercent = isDark ? 5 : 3
    const baseColor = isDark ? '#ffffff' : '#000000'
    return {
      bgColor: `color-mix(in srgb, ${baseColor} ${tintPercent}%, ${paperBg})`,
      textColor: theme.palette.text.primary,
      borderColor: theme.palette.divider,
      hoverColor: theme.palette.action.hover,
      stripedColor: alphaByTheme(theme.palette.action.hover, theme, 0.04, 0.04),
      // Fallbacks para los bordes semánticos si el componente los pide
      borderSoft: theme.palette.divider,
      borderMedium: theme.palette.divider,
      borderStrong: theme.palette.divider,
    }
  }

  // 2. COLORES DEL SISTEMA (Lectura O(1) desde el tema ya construido)
  if (bgColor in theme.palette) {
    const paletteColor = theme.palette[bgColor as keyof Palette] as any
    if (paletteColor && paletteColor.softBgColor) {
      return {
        bgColor: paletteColor.softBgColor,
        textColor: paletteColor.softTextColor,
        borderColor: paletteColor.borderColor,
        strongBgColor: paletteColor.strongBgColor,
        strongTextColor: paletteColor.strongTextColor,

        borderSoft: paletteColor.borderSoft,
        borderMedium: paletteColor.borderMedium,
        borderStrong: paletteColor.borderStrong,

        hoverColor: paletteColor.hoverBgColor, // Usamos la del tema
        stripedColor: paletteColor.faintBgColor, // Usamos la del tema
      }
    }
  }

  // 3. COLOR HEX PERSONALIZADO (Cálculo dinámico on-the-fly)
  const isCssFunction =
    typeof bgColor === 'string' && (bgColor.startsWith('color-mix') || bgColor.startsWith('var('))

  if (isCssFunction) {
    // Si es una variable CSS, no podemos calcular contrastes en JS. Hacemos un "best effort"
    const softTint = isDark ? COLOR_TINTS.soft.dark : COLOR_TINTS.soft.light
    const strongTint = isDark ? COLOR_TINTS.strong.dark : COLOR_TINTS.strong.light
    return {
      bgColor: `color-mix(in srgb, ${bgColor} ${softTint}%, ${paperBg})`,
      textColor: isDark ? '#ffffff' : '#000000',
      strongBgColor: `color-mix(in srgb, ${bgColor} ${strongTint}%, ${paperBg})`,
      strongTextColor: isDark ? '#ffffff' : '#000000',
      borderColor: theme.palette.divider,
      hoverColor: theme.palette.action.hover,
      stripedColor: alphaByTheme(theme.palette.action.hover, theme, 0.04, 0.04),
      borderSoft: theme.palette.divider,
      borderMedium: theme.palette.divider,
      borderStrong: theme.palette.divider,
    }
  }

  // Si es un HEX válido, delegamos al motor centralizado
  const dynamicStates = generateAdvancedStatesFromHex(bgColor, paperBg, isDark)
  return {
    bgColor: dynamicStates.softBgColor,
    textColor: dynamicStates.softTextColor,
    strongBgColor: dynamicStates.strongBgColor,
    strongTextColor: dynamicStates.strongTextColor,

    borderColor: dynamicStates.borderColor,
    borderSoft: dynamicStates.borderSoft,
    borderMedium: dynamicStates.borderMedium,
    borderStrong: dynamicStates.borderStrong,

    hoverColor: dynamicStates.hoverBgColor,
    stripedColor: dynamicStates.faintBgColor,
  }
}
